import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Plus, X, GripVertical, Calendar, Flag, CheckSquare, Square,
  Trash2, ChevronDown, ChevronRight, MoreHorizontal, Clock,
  AlertTriangle, Pencil, LayoutGrid
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = { "Content-Type": "application/json" };
const cred = { credentials: "include" };

const STATUSES = [
  { id: "todo", label: "To Do", color: "#8A7E6B", bg: "#F6F1E5" },
  { id: "in_progress", label: "In Progress", color: "#B5793F", bg: "#FFF8EE" },
  { id: "done", label: "Done", color: "#5C8A5C", bg: "#F0F7F0" },
];
const PRIORITIES = [
  { id: "high", label: "High", color: "#C0392B", bg: "#C0392B18" },
  { id: "medium", label: "Medium", color: "#E2A84B", bg: "#E2A84B18" },
  { id: "low", label: "Low", color: "#8A7E6B", bg: "#8A7E6B18" },
];
const PRI_MAP = Object.fromEntries(PRIORITIES.map((p) => [p.id, p]));

function isOverdue(d) {
  if (!d) return false;
  return new Date(d) < new Date() && new Date(d).toDateString() !== new Date().toDateString();
}

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ── Task Card ─────────────────────────────────────────────────────── */
function TaskCard({ task, onEdit, onDragStart }) {
  const pri = PRI_MAP[task.priority] || PRI_MAP.medium;
  const overdue = isOverdue(task.due_date) && task.status !== "done";
  const subs = task.subtasks || [];
  const subsDone = subs.filter((s) => s.done).length;

  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", task.id); onDragStart?.(task.id); }}
      onClick={() => onEdit(task)}
      className="bg-white rounded-xl border border-[#d4cbb8] px-4 py-3.5 cursor-pointer group transition-all hover:shadow-md hover:-translate-y-0.5 active:shadow-sm"
      style={{ marginBottom: "10px" }}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="mt-0.5 text-[#d4cbb8] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 cursor-grab" />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-snug ${task.status === "done" ? "line-through text-[#8A7E6B]" : "text-[#262016]"}`}>
            {task.title}
          </p>
          {task.description && (
            <p className="text-[11px] text-[#8A7E6B] mt-1 line-clamp-2">{task.description}</p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                  style={{ background: pri.bg, color: pri.color }}>
              <Flag size={8} /> {pri.label}
            </span>
            {task.due_date && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${overdue ? "bg-red-100 text-red-600" : "bg-[#26201608] text-[#8A7E6B]"}`}>
                {overdue ? <AlertTriangle size={8} /> : <Calendar size={8} />} {fmtDate(task.due_date)}
              </span>
            )}
            {subs.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-[#26201608] text-[#8A7E6B]">
                <CheckSquare size={8} /> {subsDone}/{subs.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Column ────────────────────────────────────────────────────────── */
function KanbanColumn({ status, tasks, onEdit, onDragStart, onDrop, onAddTask }) {
  const [dragOver, setDragOver] = useState(false);
  const st = STATUSES.find((s) => s.id === status);

  return (
    <div
      className="flex-1 min-w-[280px] max-w-[420px] flex flex-col"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onDrop(e.dataTransfer.getData("text/plain"), status, tasks.length); }}
      data-testid={`kanban-col-${status}`}
    >
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: st.color }} />
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#262016]">{st.label}</span>
        <span className="text-[10px] font-semibold text-[#8A7E6B] bg-[#26201608] px-2 py-0.5 rounded-full">{tasks.length}</span>
        <div className="flex-1" />
        <button onClick={() => onAddTask(status)} data-testid={`add-task-${status}`}
                className="w-6 h-6 rounded-full flex items-center justify-center text-[#8A7E6B] hover:bg-[#26201610] hover:text-[#262016] transition-colors">
          <Plus size={14} />
        </button>
      </div>

      <div
        className="flex-1 rounded-xl p-2.5 min-h-[120px] transition-colors"
        style={{ background: dragOver ? st.color + "15" : st.bg, border: `2px dashed ${dragOver ? st.color : "transparent"}` }}
      >
        {tasks.map((t) => (
          <TaskCard key={t.id} task={t} onEdit={onEdit} onDragStart={onDragStart} />
        ))}
        {tasks.length === 0 && (
          <div className="text-center py-8">
            <p className="text-[11px] text-[#8A7E6B]">No tasks yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Task Editor Modal ─────────────────────────────────────────────── */
function TaskEditor({ task, boards, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    status: task?.status || "todo",
    priority: task?.priority || "medium",
    board: task?.board || "personal",
    due_date: task?.due_date || "",
    subtasks: task?.subtasks || [],
  });
  const [newSub, setNewSub] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addSubtask = () => {
    const text = newSub.trim();
    if (!text) return;
    set("subtasks", [...form.subtasks, { id: crypto.randomUUID?.() || String(Date.now()), text, done: false }]);
    setNewSub("");
  };

  const toggleSub = (id) => {
    set("subtasks", form.subtasks.map((s) => s.id === id ? { ...s, done: !s.done } : s));
  };

  const removeSub = (id) => {
    set("subtasks", form.subtasks.filter((s) => s.id !== id));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose} data-testid="task-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#26201610] flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#262016]">{task?.id ? "Edit Task" : "New Task"}</h3>
          <button onClick={onClose} className="text-[#8A7E6B] hover:text-[#262016]"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Title */}
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Task title..."
                 className="w-full text-lg font-medium text-[#262016] outline-none placeholder-[#8A7E6B] border-b border-[#26201615] pb-2 focus:border-[#B5793F]"
                 data-testid="task-title-input" autoFocus />

          {/* Description */}
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                    placeholder="Add notes or description..."
                    rows={3}
                    className="w-full resize-none rounded-lg border border-[#26201618] bg-[#F6F1E5] px-3 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]"
                    data-testid="task-desc-input" />

          {/* Row: Status, Priority, Board */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#8A7E6B] mb-1 block">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)}
                      className="w-full rounded-lg border border-[#26201618] bg-white px-3 py-2 text-sm text-[#262016] outline-none"
                      data-testid="task-status-select">
                {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#8A7E6B] mb-1 block">Priority</label>
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)}
                      className="w-full rounded-lg border border-[#26201618] bg-white px-3 py-2 text-sm text-[#262016] outline-none"
                      data-testid="task-priority-select">
                {PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#8A7E6B] mb-1 block">Board</label>
              <select value={form.board} onChange={(e) => set("board", e.target.value)}
                      className="w-full rounded-lg border border-[#26201618] bg-white px-3 py-2 text-sm text-[#262016] outline-none"
                      data-testid="task-board-select">
                {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          {/* Due date */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#8A7E6B] mb-1 block">Due Date</label>
            <input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)}
                   className="w-full rounded-lg border border-[#26201618] bg-white px-3 py-2 text-sm text-[#262016] outline-none"
                   data-testid="task-due-input" />
          </div>

          {/* Subtasks / Checklist */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#8A7E6B] mb-2 block">
              Checklist {form.subtasks.length > 0 && `(${form.subtasks.filter((s) => s.done).length}/${form.subtasks.length})`}
            </label>
            {form.subtasks.length > 0 && (
              <div className="mb-2 rounded-lg border border-[#26201610] overflow-hidden">
                {/* Progress bar */}
                <div className="h-1.5 bg-[#26201608]">
                  <div className="h-full bg-[#5C8A5C] transition-all" style={{ width: `${form.subtasks.length ? (form.subtasks.filter((s) => s.done).length / form.subtasks.length * 100) : 0}%` }} />
                </div>
                {form.subtasks.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2 px-3 py-2 border-t border-[#26201608] group" data-testid={`subtask-${sub.id}`}>
                    <button onClick={() => toggleSub(sub.id)} className="shrink-0" data-testid={`subtask-toggle-${sub.id}`}>
                      {sub.done ? <CheckSquare size={16} className="text-[#5C8A5C]" /> : <Square size={16} className="text-[#8A7E6B]" />}
                    </button>
                    <span className={`flex-1 text-sm ${sub.done ? "line-through text-[#8A7E6B]" : "text-[#262016]"}`}>{sub.text}</span>
                    <button onClick={() => removeSub(sub.id)} className="opacity-0 group-hover:opacity-100 text-[#8A7E6B] hover:text-red-600 transition-opacity">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={newSub} onChange={(e) => setNewSub(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubtask(); } }}
                     placeholder="Add checklist item..."
                     className="flex-1 rounded-lg border border-[#26201618] bg-[#F6F1E5] px-3 py-2 text-sm outline-none placeholder-[#8A7E6B] focus:border-[#B5793F]"
                     data-testid="subtask-input" />
              <button onClick={addSubtask} className="px-3 py-2 rounded-lg text-xs font-semibold border border-[#26201618] text-[#5C5042] hover:bg-[#26201608]"
                      data-testid="subtask-add-btn">Add</button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#26201610] flex items-center gap-3">
          {task?.id && (
            <button onClick={() => { onDelete(task.id); onClose(); }} data-testid="task-delete-btn"
                    className="px-4 py-2 rounded-full text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50">
              <Trash2 size={12} className="inline mr-1" /> Delete
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-5 py-2 rounded-full text-xs font-semibold border border-[#26201618] text-[#5C5042]">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="task-save-btn"
                  className="px-6 py-2 rounded-full text-xs font-bold text-[#F6F1E5] disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────────────────────── */
export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [boards, setBoards] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeBoard, setActiveBoard] = useState("");
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...task} = edit
  const [newBoardName, setNewBoardName] = useState("");
  const [showNewBoard, setShowNewBoard] = useState(false);
  const dragItem = useRef(null);

  const load = useCallback(() => {
    const params = activeBoard ? `?board=${encodeURIComponent(activeBoard)}` : "";
    fetch(`${API}/api/admin/tasks${params}`, cred)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => setTasks(d.items || []))
      .catch(() => {});
    fetch(`${API}/api/admin/tasks/boards`, cred)
      .then((r) => r.ok ? r.json() : [])
      .then(setBoards)
      .catch(() => {});
    fetch(`${API}/api/admin/tasks/stats`, cred)
      .then((r) => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => {});
  }, [activeBoard]);

  useEffect(() => { load(); }, [load]);

  const saveTask = async (form) => {
    const isNew = !editing?.id;
    const url = isNew ? `${API}/api/admin/tasks` : `${API}/api/admin/tasks/${editing.id}`;
    const r = await fetch(url, { method: isNew ? "POST" : "PUT", headers: hdr, ...cred, body: JSON.stringify(form) });
    if (!r.ok) throw new Error((await r.json()).detail || "Failed");
    toast.success(isNew ? "Task created" : "Task updated");
    load();
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await fetch(`${API}/api/admin/tasks/${id}`, { method: "DELETE", ...cred });
    toast.success("Task deleted");
    load();
  };

  const handleDrop = async (taskId, newStatus, orderInCol) => {
    try {
      await fetch(`${API}/api/admin/tasks/reorder`, {
        method: "POST", headers: hdr, ...cred,
        body: JSON.stringify({ task_id: taskId, status: newStatus, order: orderInCol }),
      });
      load();
    } catch { toast.error("Move failed"); }
  };

  const createBoard = async () => {
    const name = newBoardName.trim();
    if (!name) return;
    try {
      const r = await fetch(`${API}/api/admin/tasks/boards`, { method: "POST", headers: hdr, ...cred, body: JSON.stringify({ name }) });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setNewBoardName("");
      setShowNewBoard(false);
      toast.success("Board created");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteBoard = async (id) => {
    if (!window.confirm("Delete this board? Tasks will move to Personal.")) return;
    await fetch(`${API}/api/admin/tasks/boards/${id}`, { method: "DELETE", ...cred });
    if (activeBoard === id) setActiveBoard("");
    toast.success("Board deleted");
    load();
  };

  const tasksByStatus = (status) => tasks.filter((t) => t.status === status).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-5" data-testid="admin-tasks">
      {/* Stats bar */}
      {stats && (
        <div className="flex flex-wrap gap-3" data-testid="tasks-stats">
          <div className="rp-foam bg-white/55 px-4 py-2.5 flex items-center gap-2">
            <LayoutGrid size={14} className="text-[#B5793F]" />
            <span className="text-xl font-bold text-[#262016]">{stats.total}</span>
            <span className="text-[11px] text-[#8A7E6B]">total</span>
          </div>
          {STATUSES.map((s) => (
            <div key={s.id} className="rp-foam bg-white/55 px-4 py-2.5 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-lg font-bold text-[#262016]">{stats.by_status?.[s.id] || 0}</span>
              <span className="text-[11px] text-[#8A7E6B]">{s.label}</span>
            </div>
          ))}
          {stats.overdue > 0 && (
            <div className="rp-foam bg-red-50 px-4 py-2.5 flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-500" />
              <span className="text-lg font-bold text-red-600">{stats.overdue}</span>
              <span className="text-[11px] text-red-500">overdue</span>
            </div>
          )}
        </div>
      )}

      {/* Board selector */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="board-selector">
        <button onClick={() => setActiveBoard("")} data-testid="board-all"
                className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
                style={{
                  background: !activeBoard ? "#262016" : "rgba(255,255,255,0.5)",
                  color: !activeBoard ? "#EDE7D9" : "#262016",
                  border: "1px solid " + (!activeBoard ? "#262016" : "rgba(38,32,22,0.15)"),
                }}>All Boards</button>
        {boards.map((b) => (
          <div key={b.id} className="relative group">
            <button onClick={() => setActiveBoard(b.id)} data-testid={`board-${b.id}`}
                    className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
                    style={{
                      background: activeBoard === b.id ? "#262016" : "rgba(255,255,255,0.5)",
                      color: activeBoard === b.id ? "#EDE7D9" : "#262016",
                      border: "1px solid " + (activeBoard === b.id ? "#262016" : "rgba(38,32,22,0.15)"),
                    }}>{b.name}</button>
            {!b.is_default && (
              <button onClick={(e) => { e.stopPropagation(); deleteBoard(b.id); }}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
                      data-testid={`board-delete-${b.id}`}>
                <X size={8} />
              </button>
            )}
          </div>
        ))}
        {showNewBoard ? (
          <div className="flex items-center gap-1.5">
            <input value={newBoardName} onChange={(e) => setNewBoardName(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") createBoard(); if (e.key === "Escape") setShowNewBoard(false); }}
                   placeholder="Board name..." autoFocus
                   className="px-3 py-1.5 rounded-full text-xs border border-[#B5793F] bg-white outline-none w-32"
                   data-testid="new-board-input" />
            <button onClick={createBoard} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-[#262016] text-[#EDE7D9]">Add</button>
            <button onClick={() => { setShowNewBoard(false); setNewBoardName(""); }} className="text-[#8A7E6B]"><X size={14} /></button>
          </div>
        ) : (
          <button onClick={() => setShowNewBoard(true)} data-testid="add-board-btn"
                  className="px-3 py-2 rounded-full text-xs font-semibold text-[#B5793F] border border-dashed border-[#B5793F55] hover:bg-[#B5793F08] transition-colors">
            <Plus size={12} className="inline mr-1" /> Board
          </button>
        )}
      </div>

      {/* Kanban Board */}
      <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ WebkitOverflowScrolling: "touch" }} data-testid="kanban-board">
        {STATUSES.map((s) => (
          <KanbanColumn
            key={s.id}
            status={s.id}
            tasks={tasksByStatus(s.id)}
            onEdit={(t) => setEditing(t)}
            onDragStart={(id) => { dragItem.current = id; }}
            onDrop={handleDrop}
            onAddTask={(status) => setEditing({ status })}
          />
        ))}
      </div>

      {/* Editor Modal */}
      {editing !== null && (
        <TaskEditor
          task={editing.id ? editing : editing}
          boards={boards}
          onSave={saveTask}
          onDelete={deleteTask}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
